import type {
  Chat,
  ChatClientOptions,
  ChatMessages,
  // Message,
  // StreamPlaybackRequest,
  // StreamRequest,
  // StreamResponse,
  StreamingChatClient,
} from "@storytell/cp-client";
import { createStore } from "solid-js/store";
import { StreamConverter } from "./converter";
import {
  type OperationMetadata,
  getBlock,
  getOperation,
  getOperationExpanded,
  getOperationMetadata,
  markOperationAsUserInteracted,
  setOperationMetadata,
  toggleOperationExpanded,
  updateBlocksAndOperations,
} from "./granularStores";
import { type MessageHandler, processStream } from "./messageHandler";
import { getAllOperations } from "./messageStructure";
import { type ConversationState, type Operation, createInitialConversationState, getCurrentOperation } from "./types";

// Streaming chat state management.
// Provides state, mutators, and streaming logic for chat message handling.
//
// Grouping Expectations:
// - Message structure groups blocks by operationId (see messageStructure for details).
// - Each group (BlockGroup) contains all blocks for a given operation, preserving their order of arrival.
// - These expectations are verified in tests (see messageStructure.test.ts).
//
// Usage:
//   const chat = createStreamingChat();
//   chat.startStream(stream);
//   <For each={chat.chatState.messages}>{...}</For>
//
// State includes current step and message history. Mutators allow updating step, adding/clearing messages.
// Streaming logic processes incoming message streams and updates state accordingly.

// Chat state shape for the streaming chat UI
export enum ChatStatus {
  INITIALIZING = 0,
  READY = 1,
  WAITING = 2,
  GENERATING = 3,
  ERROR = 4,
  DONE = 5,
}

// High-level UI status enum for simple state machine
export enum UIStatus {
  READY = "READY",
  GENERATING = "GENERATING",
  DONE = "DONE",
}

// Enhanced chat state using new hierarchical structure
export interface ChatState {
  // The current step or title of the chat (e.g., topic or phase)
  currentStep: string;
  // State machine status
  status: ChatStatus;
  // High-level UI status for display (simple state machine)
  uiStatus: UIStatus;
  // Optional error message
  error?: string;
  // Derived status that combines chat status with task/operation state
  currentActivity: string;
  // Whether the conversation has ended (no more messages expected)
  isComplete: boolean;
  // Core conversation structure with hierarchical messages
  conversation: ConversationState;
  // Reactivity trigger counter
  updateCounter: number;
  // Playback mode state
  playback: {
    isPlaybackMode: boolean;
    speed: number;
  };
  // UI state for operation interactions
  ui: {
    // Operations that have been manually expanded
    manuallyExpandedOperations: Set<string>;
    // Operations that have been manually collapsed
    manuallyCollapsedOperations: Set<string>;
    // Auto-opened artifacts to prevent duplicates
    autoOpenedArtifacts: Set<string>;
    // Last operation with blocks (for auto-collapse logic)
    lastOperationWithBlocks?: string;
  };
}

// Initial state for the chat store
const initialState: ChatState = {
  currentStep: "",
  status: ChatStatus.INITIALIZING,
  uiStatus: UIStatus.READY,
  error: undefined,
  currentActivity: "",
  isComplete: false,
  conversation: createInitialConversationState(),
  updateCounter: 0,
  playback: {
    isPlaybackMode: false,
    speed: 1.0,
  },
  ui: {
    manuallyExpandedOperations: new Set<string>(),
    manuallyCollapsedOperations: new Set<string>(),
    autoOpenedArtifacts: new Set<string>(),
    lastOperationWithBlocks: undefined,
  },
};

// Core streaming chat logic without network dependencies
function createStreamingChatCore() {
  // Reactive chat state and mutator
  const [chatState, setChatState] = createStore<ChatState>(initialState);

  // Stream converter for processing SSE messages
  const converter = new StreamConverter(chatState.conversation);

  // --- Mutators ---

  // Update the current step/title in the chat state
  const updateCurrentStep = (step: string) => {
    setChatState("currentStep", step);
  };

  // Clear the conversation state
  const clearConversation = () => {
    setChatState("conversation", createInitialConversationState());
  };

  // Set conversation as complete
  const markConversationComplete = () => {
    setChatState("conversation", "isComplete", true);
  };

  // --- UI Status Management ---
  let uiStatusTimeout: number | undefined = undefined;

  // Update UI status with simple state machine
  const updateUIStatus = (newUIStatus: UIStatus) => {
    // Clear any existing timeout
    if (uiStatusTimeout !== undefined) {
      clearTimeout(uiStatusTimeout);
      uiStatusTimeout = undefined;
    }

    setChatState("uiStatus", newUIStatus);

    // If transitioning to "Done", set up automatic revert to "Ready" after 3 seconds
    if (newUIStatus === UIStatus.DONE) {
      uiStatusTimeout = setTimeout(() => {
        setChatState("uiStatus", UIStatus.READY);
        uiStatusTimeout = undefined;
      }, 3000) as unknown as number;
    }
  };

  // --- State Machine Mutators ---
  const setStatus = (status: ChatStatus, error?: string) => {
    setChatState("status", status);
    if (error) {
      setChatState("error", error);
    }

    // Update UI status based on chat status
    if (status === ChatStatus.WAITING || status === ChatStatus.GENERATING) {
      updateUIStatus(UIStatus.GENERATING);
    } else if (status === ChatStatus.DONE) {
      updateUIStatus(UIStatus.DONE);
      markConversationComplete();
    } else if (status === ChatStatus.READY) {
      updateUIStatus(UIStatus.READY);
    }

    // Pass status directly to avoid reading stale chatState
    updateCurrentActivity(status);
  };

  // Update the current activity based on task/operation state
  const updateCurrentActivity = (statusOverride?: ChatStatus) => {
    const currentStatus = statusOverride || chatState.status;
    let activity = "";

    // If we're in done/error state, check if truly complete
    if (currentStatus === ChatStatus.DONE || currentStatus === ChatStatus.ERROR) {
      setChatState("isComplete", true);
      activity = currentStatus === ChatStatus.DONE ? "Complete" : "Error occurred";
    } else if (currentStatus === ChatStatus.WAITING) {
      activity = "Starting...";
    } else if (currentStatus === ChatStatus.GENERATING) {
      // Get current operation from the conversation state
      const currentOp = getCurrentOperation(chatState.conversation);

      if (currentOp) {
        if (currentOp.progressMessage) {
          activity = currentOp.progressMessage;
        } else if (currentOp.name) {
          activity = currentOp.name;
        } else if (currentOp.currentActivity) {
          activity = currentOp.currentActivity;
        }
      }

      // Fallback
      if (!activity) {
        activity = "Generating...";
      }
    } else {
      activity = "Ready";
    }

    setChatState("currentActivity", activity);
  };

  // --- UI Operation Toggle Methods ---

  // Toggle operation expansion with user interaction tracking
  const toggleOperationExpansion = (operationId: string) => {
    toggleOperationExpanded(operationId);
    // Mark as user-interacted to prevent auto-collapse
    markOperationAsUserInteracted(operationId);
  };

  // Mark operation as manually expanded (for pill clicks)
  const markOperationAsManuallyExpanded = (operationId: string) => {
    setChatState("ui", "manuallyExpandedOperations", (prev) => {
      const newSet = new Set(prev);
      newSet.add(operationId);
      return newSet;
    });
    setChatState("ui", "manuallyCollapsedOperations", (prev) => {
      const newSet = new Set(prev);
      newSet.delete(operationId);
      return newSet;
    });
    // Mark as user-interacted to prevent auto-collapse
    markOperationAsUserInteracted(operationId);
  };

  // Add artifact to auto-opened set
  const markArtifactAsAutoOpened = (artifactId: string) => {
    setChatState("ui", "autoOpenedArtifacts", (prev) => {
      const newSet = new Set(prev);
      newSet.add(artifactId);
      return newSet;
    });
  };

  // Update last operation with blocks
  const setLastOperationWithBlocks = (operationId: string) => {
    setChatState("ui", "lastOperationWithBlocks", operationId);
  };

  // Set playback mode and speed
  const setPlaybackMode = (isPlaybackMode: boolean, speed = 1.0) => {
    setChatState("playback", "isPlaybackMode", isPlaybackMode);
    setChatState("playback", "speed", speed);
  };

  // --- Helper Functions ---

  // Get operation title using priority order
  const getOperationTitle = (operation: Operation): string => {
    // Priority 1: Operation name (from messageStartData.title or explicit setting)
    if (operation.name && !operation.name.startsWith("Operation ")) {
      return operation.name;
    }

    // Priority 2: Task name (fallback for cases without explicit title)
    if (operation.taskName) {
      return operation.taskName;
    }

    // Priority 3: Best title from blocks
    for (const block of operation.blocks) {
      if (block.title && !block.title.startsWith("Operation ") && block.title !== "Generating artifact...") {
        return block.title;
      }
    }

    // Priority 4: Activity from first block
    const firstBlock = operation.blocks[0];
    if (firstBlock?.activity) {
      return firstBlock.activity;
    }

    // Fallback
    return `Operation ${operation.id?.slice(-8) || "unknown"}`;
  };

  // Check if operation is processing
  const isOperationProcessing = (operation: Operation): boolean => {
    return (
      operation.status !== 3 && // Not COMPLETED
      (operation.taskStatus === 1 || // PLANNED
        operation.taskStatus === 2 || // RUNNING
        operation.blocks.some((block) => !block.isComplete))
    );
  };

  // --- Message Handlers for processStream ---

  // Update conversation state from converter and sync granular stores
  const updateConversationState = (newState: ConversationState) => {
    // Check if there's a new completion operation being added
    const operations = getAllOperations(newState);
    const oldOperations = getAllOperations(chatState.conversation);
    const completionOp = operations.find(
      (op) => op.activity === "conversation_complete" || op.taskId === "end_turn_v1",
    );
    const hadCompletionOp = oldOperations.find(
      (op) => op.activity === "conversation_complete" || op.taskId === "end_turn_v1",
    );

    setChatState("conversation", newState);

    // Force deep reactivity by updating messages array reference
    setChatState("conversation", "messages", [...newState.messages]);

    // Force reactivity by incrementing counter - be more aggressive for completion operations
    setChatState("updateCounter", (prev) => {
      return prev + 1;
    });

    // If we're adding a completion operation, force an additional update to ensure reactivity
    if (completionOp && !hadCompletionOp) {
      // Force another reactivity trigger
      setTimeout(() => {
        setChatState("updateCounter", (prev) => prev + 1);
      }, 0);
    }

    // Sync granular stores with the new state
    updateBlocksAndOperations(operations);

    // Update operation metadata for efficient rendering
    for (const operation of operations) {
      const metadata: OperationMetadata = {
        title: getOperationTitle(operation),
        isProcessing: isOperationProcessing(operation),
        hasBlocks: operation.blocks.length > 0,
        maxDurationMs: operation.maxDurationMs,
        minDurationMs: operation.minDurationMs,
        expectedDurationMs: operation.estimatedDurationMs,
        durationConfidence: operation.durationConfidence,
        taskCategory: operation.durationCategory,
      };
      setOperationMetadata(operation.id, metadata);
    }
  };

  // Handlers for different message events in the stream
  const messageHandlers: MessageHandler = {
    // On message start, update step if title present and process with converter
    onMessageStart: (message: ChatMessages.Message) => {
      // On first message, transition to generating
      setChatState("status", ChatStatus.GENERATING);
      if (message.data?.case === "messageStartData") {
        const startData = message.data.value;
        if (startData?.title) {
          updateCurrentStep(startData.title);
        }
      }

      const newState = converter.handleMessageStart(message);
      updateConversationState(newState);
      updateCurrentActivity(ChatStatus.GENERATING);
    },

    // Handle complete message events (EVENT_MESSAGE)
    onMessage: (message: ChatMessages.Message) => {
      const newState = converter.handleMessage(message);
      updateConversationState(newState);
    },

    // Handle block start events
    onBlockStart: (message: ChatMessages.Message) => {
      const newState = converter.handleBlockStart(message);
      updateConversationState(newState);
      updateCurrentActivity();
    },

    // Handle delta events (text streaming)
    onDelta: (message: ChatMessages.Message) => {
      const newState = converter.handleDelta(message);
      updateConversationState(newState);
    },

    // Handle heartbeat deltas (status updates) - currently no-op for new structure
    onHeartbeatDelta: (message: ChatMessages.Message) => {
      // Heartbeat deltas are mainly for status updates, which we handle via operation status
      // For now, we'll skip these in the new structure
    },

    // Handle block stop events
    onBlockStop: (message: ChatMessages.Message) => {
      const newState = converter.handleBlockStop(message);
      updateConversationState(newState);

      // TEMPORARILY DISABLED: Auto-collapse on block completion
      // Check if this block completion means the operation is now complete
      // if (message.operationId) {
      //   setTimeout(() => {
      //     const operation = getOperation(message.operationId)();
      //
      //     // Check if all blocks in the operation are complete
      //     const allBlocksComplete = operation?.blocks.every((block) => block.isComplete) || false;
      //
      //     if (allBlocksComplete && operation) {
      //       const hasUserInteracted = getOperationUserInteracted(message.operationId)();
      //
      //       // Check if operation contains any artifact blocks
      //       const hasArtifacts = operation.blocks.some(
      //         (block) =>
      //           block.preferredRenderer === BlockRenderer.ARTIFACT_VIEWER ||
      //           block.messageType === MT.ARTIFACT_CREATION ||
      //           block.messageType === MT.ARTIFACT_REVISION,
      //       );
      //
      //       if (!hasUserInteracted && !hasArtifacts) {
      //         setOperationExpanded(message.operationId!, false);
      //       }
      //     }
      //   }, 500);
      // }
    },

    // Handle message stop events
    onMessageStop: (message: ChatMessages.Message) => {
      const newState = converter.handleMessageStop(message);
      updateConversationState(newState);
      setStatus(ChatStatus.DONE);
    },

    // Handle task creation and update events
    onTaskCreate: (message: ChatMessages.Message) => {
      const newState = converter.handleTaskEvent(message);
      updateConversationState(newState);
      updateCurrentActivity();
    },

    onTaskUpdate: (message: ChatMessages.Message) => {
      const newState = converter.handleTaskEvent(message);
      updateConversationState(newState);
      updateCurrentActivity();
    },

    // Handle operational status events
    onOperationStart: (message: ChatMessages.Message) => {
      const newState = converter.handleOperationStatus(message);
      updateConversationState(newState);
      updateCurrentActivity();
    },

    onOperationProgress: (message: ChatMessages.Message) => {
      const newState = converter.handleOperationStatus(message);
      updateConversationState(newState);
      updateCurrentActivity();
    },

    onOperationComplete: (message: ChatMessages.Message) => {
      const newState = converter.handleOperationStatus(message);
      updateConversationState(newState);
      updateCurrentActivity();

      // TEMPORARILY DISABLED: Auto-collapse on operation completion
      // Auto-collapse operation if user hasn't interacted with any blocks AND it doesn't contain artifacts
      // if (message.operationId) {
      //   setTimeout(() => {
      //     const hasUserInteracted = getOperationUserInteracted(message.operationId)();
      //     const operation = getOperation(message.operationId)();
      //
      //     // Ensure operation is actually complete
      //     const isOperationComplete =
      //       operation &&
      //       (operation.status === 3 || // COMPLETED status
      //         operation.completedAt ||
      //         operation.blocks.every((block) => block.isComplete));
      //
      //     if (!isOperationComplete) {
      //       return; // Don't collapse if operation isn't actually complete
      //     }
      //
      //     // Check if operation contains any artifact blocks
      //     const hasArtifacts =
      //       operation?.blocks.some(
      //         (block) =>
      //           block.preferredRenderer === BlockRenderer.ARTIFACT_VIEWER ||
      //           block.messageType === MT.ARTIFACT_CREATION ||
      //           block.messageType === MT.ARTIFACT_REVISION,
      //       ) || false;
      //
      //     if (!hasUserInteracted && !hasArtifacts) {
      //       setOperationExpanded(message.operationId!, false);
      //     }
      //   }, 500);
      // }
    },

    onOperationFailed: (message: ChatMessages.Message) => {
      const newState = converter.handleOperationStatus(message);
      updateConversationState(newState);
      updateCurrentActivity();
    },

    onOperationCancelled: (message: ChatMessages.Message) => {
      const newState = converter.handleOperationStatus(message);
      updateConversationState(newState);
      updateCurrentActivity();
    },

    // Handle metrics - accumulate in real-time for user feedback
    onMetrics: (
      ingressBytes: bigint,
      ingressTokens: bigint,
      egressBytes: bigint,
      egressTokens: bigint,
      blockId: string,
    ) => {
      // Create a synthetic message to trigger metric accumulation
      const syntheticMessage = {
        id: chatState.conversation.currentMessageId || "",
        blockId,
        operationId: chatState.conversation.currentOperationId || "",
        role: 0, // Default role
        index: 0,
        event: 0, // Default event
        ingressTokens,
        egressTokens,
        ingressBytes,
        egressBytes,
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        data: { case: "messageDeltaData", value: { text: "" } } as any,
      };

      // Process through converter to accumulate metrics
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      const newState = converter.handleDelta(syntheticMessage as any);
      updateConversationState(newState);
    },
  };

  // --- Streaming logic ---

  // Start processing a stream of chat messages (live or playback)
  // Updates state and structure via handlers
  const startStream = async (
    stream: AsyncIterable<Chat.StreamResponse> | AsyncIterable<Chat.StreamPlaybackResponse>,
  ) => {
    setStatus(ChatStatus.WAITING);
    clearConversation(); // Clear any previous conversation
    converter.reset(createInitialConversationState()); // Reset converter
    try {
      await processStream(stream, messageHandlers);
    } catch (error: unknown) {
      // Handle error state (reset conversation)
      clearConversation();
      converter.reset(createInitialConversationState());
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      setStatus(ChatStatus.ERROR, errorMessage);
    }
  };

  // Core streaming logic exposed for both production and testing
  const processStreamData = async (
    stream: AsyncIterable<Chat.StreamResponse> | AsyncIterable<Chat.StreamPlaybackResponse>,
  ) => {
    return await startStream(stream);
  };

  // --- Initialization ---
  // Immediately transition from initializing to ready
  setStatus(ChatStatus.READY);

  // Expose core state and streaming logic
  return {
    chatState, // Reactive chat state with hierarchical conversation
    updateCurrentStep, // Mutator for step
    clearConversation, // Mutator for clearing conversation
    startStream, // Streaming logic (low-level)
    processStreamData, // Process any async iterable stream
    setStatus, // Expose for advanced use
    updateCurrentActivity, // Manually update activity (for external triggers)
    // UI operation toggle methods
    toggleOperationExpansion, // Toggle operation expand/collapse with user interaction tracking
    markOperationAsManuallyExpanded, // Mark operation as manually expanded
    markArtifactAsAutoOpened, // Mark artifact as auto-opened
    setLastOperationWithBlocks, // Update last operation with blocks
    setPlaybackMode, // Set playback mode and speed
    // Granular store access
    getOperationExpanded,
    getBlock,
    getOperation,
    getOperationMetadata,
  };
}

/**
 * Factory function to create a streaming chat store with optional network client
 *
 * @example
 * ```typescript
 * import { useChatStreamingClient, newStreamRequest } from "@storytell/cp-client";
 * import { createStreamingChat } from "@storytell/streaming";
 *
 * const chatClient = useChatStreamingClient();
 * const chat = createStreamingChat(chatClient);
 *
 * // Type-safe with auth support
 * await chat.startChat(
 *   newStreamRequest("", "Hello", languageRegion, false),
 *   { bearerToken: "your-auth-token" }
 * );
 * ```
 */
export function createStreamingChat(chatClient?: StreamingChatClient) {
  const core = createStreamingChatCore();

  // --- Network logic ---
  // These require a chatClient to be passed in
  const startChat = async (params: Chat.StreamRequest, options?: ChatClientOptions) => {
    if (!chatClient) throw new Error("chatClient not provided");
    core.setPlaybackMode(false, 1.0); // Not playback mode
    core.setStatus(ChatStatus.WAITING);
    try {
      const stream = chatClient.stream(params, options);
      await core.startStream(stream);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      core.setStatus(ChatStatus.ERROR, errorMessage);
    }
  };

  const startPlayback = async (params: Chat.StreamPlaybackRequest, options?: ChatClientOptions) => {
    if (!chatClient) throw new Error("chatClient not provided");
    core.setPlaybackMode(true, params.playbackSpeed); // Set playback mode with speed
    core.setStatus(ChatStatus.WAITING);
    try {
      const stream = chatClient.streamPlayback(params, options);
      await core.startStream(stream);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      core.setStatus(ChatStatus.ERROR, errorMessage);
    }
  };

  // Expose all core functionality plus network methods
  return {
    ...core,
    startChat, // High-level: start a new chat
    startPlayback, // High-level: start playback
  };
}
